import { useState } from "react";
import { reaction } from "mobx";
import { authStore, leonardoStore } from "../stores";
import { addButtonToMessages } from "../helpers/leonardoHelper";

import K from "./../constants";

const BASE_URL = K.LEONARDO_URL;

const HEALTH_URL = `${BASE_URL}/health`;
const GREETINGS_URL = `${BASE_URL}/greetings`;
const START_CONVERSATION_URL = `${BASE_URL}/start_conversation`;
const BUY_URL = `${BASE_URL}/buy`;
const CROSS_SELL_URL = `${BASE_URL}/cross_selling`;
const INSURANCE_URL = `${BASE_URL}/insurance`;

/**
 * Shows a standard error message using updateMessage or setMessages.
 *
 * @param {Function|null} updateMessage - Optional updateMessage function.
 * @param {Function|null} setMessages - Optional setMessages fallback.
 */
function showErrorMessage(updateMessage, setMessages) {
  const errorMessage = {
    position: "left",
    type: "text",
    text: "Si è verificato un errore. Riprova più tardi.",
    timestamp: Date.now(),
  };

  if (typeof updateMessage === "function") {
    updateMessage(() => errorMessage, false);
  } else if (typeof setMessages === "function") {
    setMessages((prev) => [...prev, errorMessage]);
  }
}

/**
 * Validates the fetch response for SSE usage.
 * Ensures the response has a 200 status and a readable body stream.
 * If invalid, logs the error and displays a generic message to the user.
 *
 * @param {Response} response - The fetch response object.
 * @param {Function} setMessages - React state setter for chat messages.
 * @param {string} context - Optional label for identifying the request in logs.
 * @throws {Error} - If the response is invalid.
 * @returns {ReadableStreamDefaultReader} - The reader for the response body stream.
 */
function checkResponse(
  response,
  updateMessage = null,
  setMessages = null,
  context = ""
) {
  if (!response.ok) {
    console.error(`HTTP error (${response.status}) during ${context}`);
    showErrorMessage(updateMessage, setMessages);
    throw new Error(`Invalid status ${response.status} in ${context}`);
  }

  if (!response.body) {
    console.error(`Missing response body during ${context}`);
    showErrorMessage(updateMessage, setMessages);
    throw new Error(`Missing response body in ${context}`);
  }

  return response.body.getReader();
}

/**
 * Executes a fetch with a timeout using AbortController.
 *
 * @param {string} url - The URL to fetch.
 * @param {Object} options - Fetch options (headers, method, body, etc.).
 * @param {number} timeoutMs - Timeout in milliseconds, defaults to 20000ms (20s).
 * @returns {Promise<Response>} The fetch response.
 * @throws {Error} If the fetch times out or fails.
 */
function fetchWithTimeout(url, options = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeout)
  );
}

/**
 * Returns the default headers for fetch requests.
 *
 * @returns {Object} The default headers object.
 */
export function getDefaultHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: authStore.token,
  };
}

/**
 * Builds the messages payload for the request body.
 *
 * @param {Array} messages - The current messages state.
 * @param {string} text - The new message text to be appended.
 * @returns {Array} The built messages payload.
 */
function buildMessagesPayload(messages, text) {
  return [
    ...messages,
    { position: "right", type: "text", text, timestamp: Date.now() },
  ]
    .filter((m) => m && m.text != null)
    .map((m) => [m.position === "right" ? "user" : "ai", m.text]);
}

/**
 * Adds a loading message to the chat and sets the chatting state to active.
 * Returns a function to update the loading message.
 *
 * @param {Function} setMessages - Function to update the messages state.
 * @param {Function} setIsChatting - Function to update the chatting status.
 * @returns {Function} updateLoadingMessage(updateCallback, isFinal)
 */
function addLoadingMessage(setMessages, setIsChatting) {
  let loadingMsgIndex;
  setMessages((prevMessages) => {
    const updatedMessages = [
      ...prevMessages,
      { position: "left", type: "loading", text: "...", timestamp: Date.now() },
    ];
    loadingMsgIndex = updatedMessages.length - 1;
    return updatedMessages;
  });
  setIsChatting(true);

  /**
   * Updates the loading message and, if final, stops the chatting state.
   *
   * @param {Function} updateCallback - Callback that returns the updated message object.
   * @param {boolean} isFinal - Indicates whether the update is final.
   */
  return (updateCallback, isFinal = false) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg, idx) =>
        idx === loadingMsgIndex ? updateCallback(msg) : msg
      )
    );
    if (isFinal) {
      setIsChatting(false);
    }
  };
}

/**
 * Handles the SSE (Server-Sent Events) stream response.
 *
 * @param {ReadableStreamDefaultReader} reader - The stream reader.
 * @param {Function} updateLoadingMessage - Function to update the loading message.
 * @param {Function} onEvent - Callback to process each parsed event data.
 * @param {Function} onDone - Callback to process when the stream is done.
 * @param {Function} doneFlagFn - Function to determine if the stream is done.
 * @returns {Promise<void>}
 */
async function handleSSEStream(
  reader,
  updateLoadingMessage,
  onEvent = () => {},
  onDone = () => {},
  doneFlagFn = () => true
) {
  const decoder = new TextDecoder();
  let botText = "";
  let collectedEventData = {};
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      if (buffer.trim() !== "") {
        try {
          const eventData = JSON.parse(buffer.trim());
          collectedEventData = { ...collectedEventData, ...eventData };
          onEvent(eventData);
        } catch (err) {
          console.error("Final incomplete buffer error:", err, buffer);
        }
      }
      updateLoadingMessage(
        () => ({
          position: "left",
          type: "text",
          text: botText,
          timestamp: Date.now(),
        }),
        doneFlagFn()
      );
      onDone(collectedEventData);
      break;
    }
    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;

    let lines = buffer.split("\n");

    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      if (!line.startsWith("data:")) continue;

      const dataContent = line.slice(5).trim();
      if (dataContent === "") continue;

      try {
        const eventData = JSON.parse(dataContent);
        if (eventData.message) {
          botText += eventData.message;
          updateLoadingMessage(() => ({
            position: "left",
            type: "text",
            text: botText,
            timestamp: Date.now(),
          }));
        }
        collectedEventData = { ...collectedEventData, ...eventData };
        onEvent(eventData);
      } catch (err) {
        console.error("Error parsing SSE event:", err, dataContent);
      }
    }

    buffer = lines[lines.length - 1];
  }
}

/**
 * Updates the leonardoStore based on event data.
 *
 * @param {Object} eventData - The event data object from the stream.
 * @param {string} type - The type of request ("start_conversation", "buy", "cross_selling").
 */
function updateLeonardoStore(eventData, type) {
  switch (type) {
    case "greetings":
      if (eventData.user !== undefined) {
        leonardoStore.user = eventData.user;
      }
      break;
    case "start_conversation":
      if (eventData.plate_number !== undefined) {
        leonardoStore.selectedPlateNumber = eventData.plate_number;
      }
      if (eventData.contract_name !== undefined) {
        leonardoStore.selectedContractName = eventData.contract_name;
      }
      if (eventData.buy_interest !== undefined) {
        leonardoStore.selectedBuyInterest = eventData.buy_interest;
      }
      break;
    case "buy":
      if (eventData.plate_number !== undefined) {
        leonardoStore.selectedPlateNumber = eventData.plate_number;
      }
      if (eventData.contract_name !== undefined) {
        leonardoStore.selectedContractName = eventData.contract_name;
      }
      if (eventData.buy_interest !== undefined) {
        leonardoStore.selectedBuyInterest = eventData.buy_interest;
      }
      if (eventData.buy_confirmation !== undefined) {
        leonardoStore.selectedBuyConfirmation = eventData.buy_confirmation;
      }
      break;
    case "cross_selling":
      if (eventData.accepted_product_names !== undefined) {
        leonardoStore.userAcceptedProductNames =
          eventData.accepted_product_names;
      }
      if (eventData.accepted_product_names_charge_model !== undefined) {
        leonardoStore.userAcceptedChargeModels =
          eventData.accepted_product_names_charge_model;
      }
      if (eventData.rejected_product_names !== undefined) {
        leonardoStore.userRejectedProductNames =
          eventData.rejected_product_names;
      }
      if (eventData.product_names !== undefined) {
        leonardoStore.product_names = eventData.product_names;
      }
      if (eventData.confirmed_last_cross_selling !== undefined) {
        leonardoStore.lastCrossSellingConfirmed =
          eventData.confirmed_last_cross_selling;
      }
      if (eventData.product_name_offered !== undefined) {
        leonardoStore.previousOfferedProductName =
          eventData.product_name_offered;
      }
      if (eventData.airClubGeneral !== undefined) {
        leonardoStore.airClubGeneral = eventData.airClubGeneral;
      }
      if (eventData.air_club_profiling !== undefined) {
        leonardoStore.air_club_profiling = eventData.air_club_profiling;
      }
      if (eventData.air_club_marketing !== undefined) {
        leonardoStore.air_club_marketing = eventData.air_club_marketing;
      }
      if (eventData.air_club_third_parties !== undefined) {
        leonardoStore.air_club_third_parties = eventData.air_club_third_parties;
      }
      if (eventData.completed_cross_selling !== undefined) {
        leonardoStore.crossSellingCompleted = eventData.completed_cross_selling;
      }
      break;
    case "insurance":
      if (eventData.collected_data !== undefined) {
        leonardoStore.leonardoInsurance = eventData.collected_data;
      }
      break;
    default:
      console.warn(`Unknown event type: ${type}`);
      break;
  }

  if (eventData.cart !== undefined) {
    leonardoStore.cart = eventData.cart;
  }
}

/**
 * Custom hook to manage the conversation with the backend API.
 *
 * @returns {Object} An object containing:
 *  - messages: Array of chat messages.
 *  - sendGreetings: Function to trigger the greeting message.
 *  - sendMessage: Function to send a user message and process the response.
 *  - isChatting: Boolean indicating if a chat response is being processed.
 */
export const useConversation = () => {
  const [messages, setMessages] = useState([]);
  const [isChatting, setIsChatting] = useState(false);

  // Helper to add a loading message
  const updateLoadingMessage = () =>
    addLoadingMessage(setMessages, setIsChatting);

  /**
   * Sends a greeting message request to the backend and processes the plain text response stream.
   */
  const sendGreetings = async () => {
    const updateMessage = updateLoadingMessage();
    const { isSellingInsurance } = leonardoStore;

    if (isSellingInsurance) {
      updateMessage(
        () => ({
          position: "left",
          type: "text",
          text: "Ciao, sei interessato a un'assicurazione?",
          timestamp: Date.now(),
        }),
        null
      );

      updateMessage(
        () => ({
          position: "left",
          type: "insurance-field",
          timestamp: Date.now(),
        }),
        null
      );

      setIsChatting(false);

      return { success: true };
    } else {
      try {
        const {
          attributes: { "custom:external_uuid": uuid },
        } = authStore.user ? authStore.user : {};

        const response = await fetch(`${GREETINGS_URL}/${uuid}`, {
          method: "GET",
          headers: getDefaultHeaders(),
        });

        if (!response.ok) {
          return { success: false };
        }

        if (!response.body) {
          return { success: false };
        }

        const reader = response.body.getReader();
        await handleSSEStream(
          reader,
          updateMessage,
          (eventData) => {
            updateLeonardoStore(eventData, "greetings");
          },
          (eventData) => {
            addButtonToMessages(
              eventData,
              "greetings",
              setMessages,
              sendMessage
            );
          }
        );
        return { success: true };
      } catch (error) {
        console.error("Streaming error in sendGreetings:", error);
        return { success: false };
      }
    }
  };

  /**
   * Sends a conversation start request with the user message and processes the SSE stream response.
   *
   * @param {string} text - The user's message.
   */
  const sendConversationStart = async (text) => {
    const updateMessage = updateLoadingMessage();

    try {
      const {
        attributes: { "custom:external_uuid": uuid },
      } = authStore.user ? authStore.user : {};

      const response = await fetchWithTimeout(
        `${START_CONVERSATION_URL}/${uuid}`,
        {
          method: "POST",
          headers: getDefaultHeaders(),
          cache: "no-store",
          body: JSON.stringify({
            plate_number: leonardoStore.selectedPlateNumber,
            contract_name: leonardoStore.selectedContractName,
            buy_interest: leonardoStore.selectedBuyInterest,
            messages: buildMessagesPayload(messages, text),
          }),
        }
      );

      const reader = checkResponse(
        response,
        updateMessage,
        setMessages,
        "start_conversation"
      );

      await handleSSEStream(
        reader,
        updateMessage,
        (eventData) => {
          updateLeonardoStore(eventData, "start_conversation");
        },
        (eventData) => {
          addButtonToMessages(
            eventData,
            "start_conversation",
            setMessages,
            sendMessage
          );
        }
      );
    } catch (error) {
      showErrorMessage(updateMessage, setMessages);
      console.error("Streaming error in sendConversationStart:", error);
    }
  };

  /**
   * Sends a buy request based on the user's message and processes the SSE stream response.
   *
   * @param {string} text - The user's message.
   */
  const sendBuyRequest = async (text) => {
    const updateMessage = updateLoadingMessage();

    try {
      const {
        attributes: { "custom:external_uuid": uuid },
      } = authStore.user ? authStore.user : {};

      const response = await fetchWithTimeout(`${BUY_URL}/${uuid}`, {
        method: "POST",
        headers: getDefaultHeaders(),
        cache: "no-store",
        body: JSON.stringify({
          plate_number: leonardoStore.selectedPlateNumber,
          contract_name: leonardoStore.selectedContractName,
          buy_interest: leonardoStore.selectedBuyInterest,
          cross_selling_product_name: leonardoStore.currentOfferedProductName,
          messages: buildMessagesPayload(messages, text),
        }),
      });

      const reader = checkResponse(response, updateMessage, setMessages, "buy");

      await handleSSEStream(
        reader,
        updateMessage,
        (eventData) => {
          updateLeonardoStore(eventData, "buy");
        },
        (eventData) => {
          addButtonToMessages(eventData, "buy", setMessages, sendMessage);
        }
      );
    } catch (error) {
      showErrorMessage(updateMessage, setMessages);
      console.error("Streaming error in sendBuyRequest:", error);
    }
  };

  /**
   * Sends a cross-sell request based on the user's message and processes the SSE stream response.
   *
   * @param {string} text - The user's message.
   */
  const sendCrossSellRequest = async (text) => {
    const updateMessage = updateLoadingMessage();

    try {
      const {
        attributes: { "custom:external_uuid": uuid },
      } = authStore.user ? authStore.user : {};

      const response = await fetchWithTimeout(
        `${CROSS_SELL_URL}/assistenza_base/${uuid}`,
        {
          method: "POST",
          headers: getDefaultHeaders(),
          cache: "no-store",
          body: JSON.stringify({
            accepted_product_names:
              leonardoStore.userAcceptedProductNames || [],
            accepted_product_names_charge_model:
              leonardoStore.userAcceptedChargeModels || [],
            rejected_product_names:
              leonardoStore.userRejectedProductNames || [],
            product_names: leonardoStore.product_names || [],
            product_name_in_consideration:
              leonardoStore.currentOfferedProductName || "",
            cart: leonardoStore.cart || [],
            license_plate: leonardoStore.selectedPlateNumber || null,
            airclub_general: leonardoStore.airClubGeneral,
            air_club_profiling: leonardoStore.air_club_profiling,
            air_club_marketing: leonardoStore.air_club_marketing,
            air_club_third_parties: leonardoStore.air_club_third_parties,
            messages: buildMessagesPayload(messages, text),
            completed_cross_selling: leonardoStore.crossSellingCompleted,
          }),
        }
      );

      const reader = checkResponse(
        response,
        updateMessage,
        setMessages,
        "cross_selling"
      );

      await handleSSEStream(
        reader,
        updateMessage,
        (eventData) => {
          updateLeonardoStore(eventData, "cross_selling");
        },
        (eventData) => {
          addButtonToMessages(
            eventData,
            "cross_selling",
            setMessages,
            sendMessage
          );
        },
        () =>
          leonardoStore.lastCrossSellingConfirmed === false ||
          (leonardoStore.allPrivacyConsentsGiven &&
            leonardoStore.lastCrossSellingConfirmed)
      );

      // Update product name under consideration based on offered product
      leonardoStore.currentOfferedProductName =
        leonardoStore.previousOfferedProductName || null;

      if (leonardoStore.lastCrossSellingConfirmed) {
        leonardoStore.privacyEnabled = true;
        setMessages((prev) => [
          ...prev,
          {
            position: "left",
            type: "custom-privacy",
            timestamp: Date.now(),
          },
        ]);
      }

      if (leonardoStore.crossSellingCompleted) {
        setMessages((prev) => [
          ...prev,
          {
            position: "left",
            type: "custom-payment",
            timestamp: Date.now(),
          },
        ]);

        setIsChatting(true);
      }

      const disposer = reaction(
        () =>
          leonardoStore.allPrivacyConsentsGiven &&
          leonardoStore.selectedBuyConfirmation === true,
        (isReady) => {
          if (isReady) {
            disposer(); // Stop the reaction
            const secondMessage = "Accetto le condizioni.";
            setMessages((prev) => [
              ...prev,
              {
                position: "right",
                type: "text",
                text: secondMessage,
                timestamp: Date.now(),
              },
            ]);
            sendCrossSellRequest(secondMessage);
            leonardoStore.userHasSeenPrivacyForm = false;
            leonardoStore.checkedAgreedGeneralConditions = false;
            leonardoStore.checkedAgreedRestrictiveClauses = false;
          }
        },
        { fireImmediately: false }
      );
    } catch (error) {
      showErrorMessage(updateMessage, setMessages);
      console.error("Streaming error in sendCrossSellRequest:", error);
    }
  };

  const sendInsuranceRequest = async (text) => {
    const updateMessage = updateLoadingMessage();

    try {
      const {
        attributes: { "custom:external_uuid": uuid },
      } = authStore.user ? authStore.user : {};

      const { plateNumber, leonardoInsurance } = leonardoStore;

      const response = await fetchWithTimeout(
        `${INSURANCE_URL}/${uuid}/vehicle/${plateNumber}`,
        {
          method: "POST",
          headers: getDefaultHeaders(),
          cache: "no-store",
          body: JSON.stringify({
            messages: buildMessagesPayload(messages, text),
            collected_data: leonardoInsurance,
          }),
        }
      );

      const reader = checkResponse(
        response,
        updateMessage,
        setMessages,
        "insurance"
      );

      await handleSSEStream(
        reader,
        updateMessage,
        (eventData) => {
          updateLeonardoStore(eventData, "insurance");
        },
        (eventData) => {
          addButtonToMessages(eventData, "insurance", setMessages, sendMessage);
        }
      );
    } catch (error) {
      showErrorMessage(updateMessage, setMessages);
      console.error("Streaming error in sendInsuranceRequest:", error);
    }
  };

  /**
   * Sends a user message and determines which request to execute based on the current store state.
   *
   * @param {string} text - The user's message.
   */
  const sendMessage = async (text) => {
    if (!text.trim()) {
      return;
    }

    setMessages((prev) => [
      ...prev.filter((msg) => msg.type !== "chat-button"),
      { position: "right", type: "text", text, timestamp: Date.now() },
    ]);

    const plateNumber = leonardoStore.selectedPlateNumber;
    const contractName = leonardoStore.selectedContractName;
    const buyInterest = leonardoStore.selectedBuyInterest;
    const buyConfirmation = leonardoStore.selectedBuyConfirmation;
    const isSellingInsurance = leonardoStore.isSellingInsurance;

    if (isSellingInsurance) {
      await sendInsuranceRequest(text);
    } else if (
      plateNumber &&
      contractName &&
      buyInterest === true &&
      !buyConfirmation
    ) {
      await sendBuyRequest(text);
    } else if (
      plateNumber &&
      contractName &&
      buyInterest === true &&
      buyConfirmation === true
    ) {
      await sendCrossSellRequest(text);
    } else {
      await sendConversationStart(text);
    }
  };

  return { messages, sendGreetings, sendMessage, isChatting, setMessages };
};

/**
 * Verifica lo stato online dell'API backend.
 * @returns {Promise<boolean>} True se l'API è online, altrimenti false.
 */
export const checkHealth = async () => {
  try {
    const response = await fetchWithTimeout(HEALTH_URL);
    if (!response.ok) {
      throw new Error("API is offline");
    }
    return true;
  } catch (error) {
    console.error("Error checking health:", error);
    return false;
  }
};

export default {
  checkHealth,
  useConversation,
};
